import { useState, useEffect } from 'react'
import { router, Link, useForm } from '@inertiajs/react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Section,
  Text,
  Badge,
  EmptyState,
  Modal,
  TextField,
  Select,
  GroupedTable,
  TableCell,
  AddIcon,
  DeleteIcon,
  EditIcon,
  RefreshIcon,
  ArrowLeftIcon,
  PhoneIcon,
  CheckCircleIcon,
  FileIcon,
  type ServerPaginationProps,
} from '@/components/design-system'
import TemplatePreviewModal from './components/TemplatePreviewModal'
import type { TemplateComponent, TemplateRow } from './types'

interface WabaDetail {
  id: string
  external_id: string
  name: string | null
  organization_id: string
  organization_name: string
  webhook_subscribed: boolean
  created_at: string
  updated_at: string
}

interface PhoneRow {
  id: string
  meta_phone_number_id: string
  display_phone_number: string | null
  verified_name: string | null
  verification_status: string | null
  quality_rating: string | null
  registration_status: string | null
  digital_worker_name: string | null
  created_at: string
}

interface OrgOption {
  id: string
  name: string
}

interface Props {
  waba: WabaDetail
  phones: PhoneRow[]
  templates: TemplateRow[]
  organizations: OrgOption[]
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function verificationBadgeTone(status: string | null): 'success' | 'warning' | undefined {
  if (status === 'VERIFIED') return 'success'
  if (status) return 'warning'
  return undefined
}

function qualityBadgeTone(rating: string | null): 'success' | 'warning' | 'critical' | undefined {
  if (rating === 'GREEN') return 'success'
  if (rating === 'YELLOW') return 'warning'
  if (rating === 'RED') return 'critical'
  return undefined
}

function getRegistrationBadge(phone: PhoneRow) {
  const status = phone.registration_status

  if (status === 'registered') return <Badge tone="success">Ativo</Badge>
  if (status === 'verified' || phone.verification_status === 'VERIFIED') return <Badge tone="success">Verificado</Badge>
  if (status === 'verification_requested') return <Badge tone="warning">Verificando...</Badge>
  if (status === 'pending' || status === 'registered_with_meta') return <Badge tone="info">Pendente</Badge>
  if (status === 'failed') return <Badge tone="critical">Falhou</Badge>

  return null
}

function templateStatusTone(status: string | null): 'success' | 'warning' | 'critical' | 'info' | undefined {
  if (status === 'APPROVED') return 'success'
  if (status === 'PENDING') return 'warning'
  if (status === 'REJECTED' || status === 'DISABLED') return 'critical'
  if (status === 'PAUSED' || status === 'IN_APPEAL') return 'attention' as 'info'
  return undefined
}

function templateCategoryTone(category: string): 'info' | 'purple' | 'new' {
  if (category === 'MARKETING') return 'purple'
  if (category === 'AUTHENTICATION') return 'new'
  return 'info'
}

function getBodyText(components: TemplateComponent[]): string {
  const body = components.find(c => c.type === 'BODY')
  return body?.text || ''
}

function getButtonCount(components: TemplateComponent[]): number {
  const buttons = components.find(c => c.type === 'BUTTONS')
  return buttons?.buttons?.length || 0
}

const EDITABLE_STATUSES = ['APPROVED', 'REJECTED', 'PAUSED']

const LANGUAGE_OPTIONS = [
  { label: 'Português (BR)', value: 'pt_BR' },
  { label: 'English (US)', value: 'en_US' },
  { label: 'Español', value: 'es' },
]

const CATEGORY_OPTIONS = [
  { label: 'Marketing', value: 'MARKETING' },
  { label: 'Utility', value: 'UTILITY' },
  { label: 'Authentication', value: 'AUTHENTICATION' },
]

export default function Show({ waba, phones, templates, organizations }: Props) {
  const [editWabaOpen, setEditWabaOpen] = useState(false)
  const [deleteWabaOpen, setDeleteWabaOpen] = useState(false)
  const [createPhoneOpen, setCreatePhoneOpen] = useState(false)
  const [editPhoneOpen, setEditPhoneOpen] = useState(false)
  const [deletePhoneOpen, setDeletePhoneOpen] = useState(false)
  const [selectedPhone, setSelectedPhone] = useState<PhoneRow | null>(null)
  const [syncing, setSyncing] = useState(false)
  const [subscribing, setSubscribing] = useState(false)

  // Template state
  const [createTemplateOpen, setCreateTemplateOpen] = useState(false)
  const [editTemplateOpen, setEditTemplateOpen] = useState(false)
  const [deleteTemplateOpen, setDeleteTemplateOpen] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateRow | null>(null)
  const [syncingTemplates, setSyncingTemplates] = useState(false)
  const [previewTemplate, setPreviewTemplate] = useState<TemplateRow | null>(null)

  const editWabaForm = useForm({
    organization_id: waba.organization_id,
    external_id: waba.external_id,
    name: waba.name || '',
  })

  const phoneForm = useForm({
    verified_name: '',
    area_code: '11',
  })

  const editPhoneForm = useForm({
    meta_phone_number_id: '',
    display_phone_number: '',
    verified_name: '',
    verification_status: '',
    quality_rating: '',
  })

  const templateForm = useForm({
    name: '',
    language: 'pt_BR',
    category: 'MARKETING',
    header_text: '',
    body_text: '',
    body_example: '',
    footer_text: '',
  })

  const editTemplateForm = useForm({
    header_text: '',
    body_text: '',
    body_example: '',
    footer_text: '',
  })

  // Poll for async registration status updates
  const pendingStatuses = phones
    .map(p => p.registration_status)
    .filter(s => s && !['registered', 'failed'].includes(s))
    .join(',')

  useEffect(() => {
    if (!pendingStatuses) return

    const interval = setInterval(() => {
      router.reload({ only: ['phones'] })
    }, 10_000)

    return () => clearInterval(interval)
  }, [pendingStatuses])

  const orgOptions = organizations.map((org) => ({ label: org.name, value: org.id }))

  const assignedPhones = phones.filter((p) => p.digital_worker_name)

  // WABA actions
  const handleUpdateWaba = () => {
    editWabaForm.patch(`/cc/wabas/${waba.id}`, {
      onSuccess: () => setEditWabaOpen(false),
    })
  }

  const handleDeleteWaba = () => {
    router.delete(`/cc/wabas/${waba.id}`)
  }

  const handleSubscribeApp = () => {
    setSubscribing(true)
    router.post(`/cc/wabas/${waba.id}/subscribe_app`, {}, {
      onFinish: () => setSubscribing(false),
    })
  }

  const handleSyncPhones = () => {
    setSyncing(true)
    router.post(`/cc/wabas/${waba.id}/sync_phones`, {}, {
      onFinish: () => setSyncing(false),
    })
  }

  // Phone actions
  const handleCreatePhone = () => {
    phoneForm.post(`/cc/wabas/${waba.id}/whatsapp_phones`, {
      onSuccess: () => {
        setCreatePhoneOpen(false)
        phoneForm.reset()
      },
    })
  }

  const handleEditPhoneClick = (phone: PhoneRow) => {
    setSelectedPhone(phone)
    editPhoneForm.setData({
      meta_phone_number_id: phone.meta_phone_number_id,
      display_phone_number: phone.display_phone_number || '',
      verified_name: phone.verified_name || '',
      verification_status: phone.verification_status || '',
      quality_rating: phone.quality_rating || '',
    })
    setEditPhoneOpen(true)
  }

  const handleUpdatePhone = () => {
    if (!selectedPhone) return
    editPhoneForm.patch(`/cc/wabas/${waba.id}/whatsapp_phones/${selectedPhone.id}`, {
      onSuccess: () => {
        setEditPhoneOpen(false)
        setSelectedPhone(null)
      },
    })
  }

  const handleDeletePhoneClick = (phone: PhoneRow) => {
    setSelectedPhone(phone)
    setDeletePhoneOpen(true)
  }

  const handleDeletePhone = () => {
    if (!selectedPhone) return
    router.delete(`/cc/wabas/${waba.id}/whatsapp_phones/${selectedPhone.id}`, {
      onSuccess: () => {
        setDeletePhoneOpen(false)
        setSelectedPhone(null)
      },
    })
  }

  // Template actions
  const handleSyncTemplates = () => {
    setSyncingTemplates(true)
    router.post(`/cc/wabas/${waba.id}/sync_templates`, {}, {
      onFinish: () => setSyncingTemplates(false),
    })
  }

  const handleCreateTemplate = () => {
    templateForm.post(`/cc/wabas/${waba.id}/whatsapp_templates`, {
      onSuccess: () => {
        setCreateTemplateOpen(false)
        templateForm.reset()
      },
    })
  }

  const handleEditTemplateClick = (template: TemplateRow) => {
    setSelectedTemplate(template)
    const headerComp = template.components.find(c => c.type === 'HEADER')
    const bodyComp = template.components.find(c => c.type === 'BODY')
    const footerComp = template.components.find(c => c.type === 'FOOTER')
    editTemplateForm.setData({
      header_text: headerComp?.text || '',
      body_text: bodyComp?.text || '',
      body_example: bodyComp?.example?.body_text?.[0]?.join(', ') || '',
      footer_text: footerComp?.text || '',
    })
    setEditTemplateOpen(true)
  }

  const handleUpdateTemplate = () => {
    if (!selectedTemplate) return
    editTemplateForm.patch(`/cc/wabas/${waba.id}/whatsapp_templates/${selectedTemplate.id}`, {
      onSuccess: () => {
        setEditTemplateOpen(false)
        setSelectedTemplate(null)
      },
    })
  }

  const handleDeleteTemplateClick = (template: TemplateRow) => {
    setSelectedTemplate(template)
    setDeleteTemplateOpen(true)
  }

  const handleDeleteTemplate = () => {
    if (!selectedTemplate) return
    router.delete(`/cc/wabas/${waba.id}/whatsapp_templates/${selectedTemplate.id}`, {
      onSuccess: () => {
        setDeleteTemplateOpen(false)
        setSelectedTemplate(null)
      },
    })
  }

  // Phone table
  const phoneColumns = [
    { key: 'meta_id', title: 'Meta Phone ID', width: '180px' },
    { key: 'number', title: 'Número', width: '160px' },
    { key: 'verified_name', title: 'Nome Verificado', width: '160px' },
    { key: 'registration', title: 'Registro', width: '120px' },
    { key: 'status', title: 'Status', width: '120px' },
    { key: 'quality', title: 'Qualidade', width: '100px' },
    { key: 'worker', title: 'Digital Worker', width: '160px' },
    { key: 'actions', title: '', width: '100px' },
  ]

  const renderPhoneCells = (phone: PhoneRow) => (
    <>
      <TableCell>
        <Text size="small">{phone.meta_phone_number_id}</Text>
      </TableCell>
      <TableCell>{phone.display_phone_number || '—'}</TableCell>
      <TableCell>{phone.verified_name || '—'}</TableCell>
      <TableCell>
        {getRegistrationBadge(phone) || '—'}
      </TableCell>
      <TableCell>
        {phone.verification_status ? (
          <Badge tone={verificationBadgeTone(phone.verification_status)}>
            {phone.verification_status}
          </Badge>
        ) : '—'}
      </TableCell>
      <TableCell>
        {phone.quality_rating ? (
          <Badge tone={qualityBadgeTone(phone.quality_rating)}>
            {phone.quality_rating}
          </Badge>
        ) : '—'}
      </TableCell>
      <TableCell>
        {phone.digital_worker_name ? (
          <Badge tone="info">{phone.digital_worker_name}</Badge>
        ) : (
          <Text size="small" color="subdued">Disponível</Text>
        )}
      </TableCell>
      <TableCell>
        <div className="flex justify-end gap-2">
          {phone.registration_status !== 'registered' && (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={() => router.post(`/cc/wabas/${waba.id}/whatsapp_phones/${phone.id}/register`)}
            >
              <CheckCircleIcon />
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => handleEditPhoneClick(phone)}
          >
            <EditIcon />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => handleDeletePhoneClick(phone)}
          >
            <DeleteIcon />
          </Button>
        </div>
      </TableCell>
    </>
  )

  const phonePagination: ServerPaginationProps = {
    currentPage: 1,
    totalPages: 1,
    totalItems: phones.length,
    itemsPerPage: phones.length,
    onPageChange: () => {},
  }

  // Template table
  const templateColumns = [
    { key: 'name', title: 'Nome', width: '180px' },
    { key: 'language', title: 'Idioma', width: '100px' },
    { key: 'category', title: 'Categoria', width: '130px' },
    { key: 'status', title: 'Status', width: '130px' },
    { key: 'content', title: 'Conteúdo', width: '250px' },
    { key: 'actions', title: '', width: '80px' },
  ]

  const renderTemplateCells = (template: TemplateRow) => {
    const bodyText = getBodyText(template.components)
    const buttonCount = getButtonCount(template.components)
    const truncatedBody = bodyText.length > 60 ? bodyText.slice(0, 60) + '...' : bodyText

    return (
      <>
        <TableCell>
          <button
            className="text-left text-[13px] text-blue-600 hover:underline font-medium"
            onClick={() => setPreviewTemplate(template)}
          >
            {template.name}
          </button>
        </TableCell>
        <TableCell>{template.language}</TableCell>
        <TableCell>
          <Badge tone={templateCategoryTone(template.category)}>
            {template.category}
          </Badge>
        </TableCell>
        <TableCell>
          <div>
            {template.status ? (
              <Badge tone={templateStatusTone(template.status)}>
                {template.status}
              </Badge>
            ) : '—'}
            {template.status === 'REJECTED' && template.rejected_reason && (
              <p className="text-xs text-red-600 mt-1">{template.rejected_reason}</p>
            )}
          </div>
        </TableCell>
        <TableCell>
          <div>
            <Text size="small">{truncatedBody || '—'}</Text>
            {buttonCount > 0 && (
              <Text size="small" color="subdued">{buttonCount} botão(ões)</Text>
            )}
          </div>
        </TableCell>
        <TableCell>
          <div className="flex justify-end gap-2">
            {template.status && EDITABLE_STATUSES.includes(template.status) && (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => handleEditTemplateClick(template)}
              >
                <EditIcon />
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={() => handleDeleteTemplateClick(template)}
            >
              <DeleteIcon />
            </Button>
          </div>
        </TableCell>
      </>
    )
  }

  const templatePagination: ServerPaginationProps = {
    currentPage: 1,
    totalPages: 1,
    totalItems: templates.length,
    itemsPerPage: templates.length,
    onPageChange: () => {},
  }

  // Template name validation: only lowercase + numbers + underscores
  const handleTemplateNameChange = (value: string) => {
    templateForm.setData('name', value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link href="/cc/wabas">
          <Button type="button" variant="ghost" size="icon">
            <ArrowLeftIcon />
          </Button>
        </Link>
        <div>
          <Text as="h1" size="display-small">{waba.name || waba.external_id}</Text>
          {waba.name && (
            <Text color="subdued" size="small">{waba.external_id}</Text>
          )}
        </div>
      </div>

      {/* WABA Details */}
      <Section
        title="Detalhes da WABA"
        headerActions={
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setEditWabaOpen(true)}>
              <EditIcon />
              Editar
            </Button>
            <Button type="button" variant="destructive" size="sm" onClick={() => setDeleteWabaOpen(true)}>
              <DeleteIcon />
              Excluir
            </Button>
          </div>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Text size="small" color="subdued">External ID</Text>
            <Text>{waba.external_id}</Text>
          </div>
          <div>
            <Text size="small" color="subdued">Organização</Text>
            <Text>{waba.organization_name}</Text>
          </div>
          <div>
            <Text size="small" color="subdued">Webhook</Text>
            <div className="flex items-center gap-2 mt-1">
              {waba.webhook_subscribed ? (
                <Badge tone="success">Webhook conectado</Badge>
              ) : (
                <>
                  <Badge tone="warning">Webhook não conectado</Badge>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleSubscribeApp}
                    disabled={subscribing}
                  >
                    {subscribing && <Loader2 className="h-4 w-4 animate-spin" />}
                    Inscrever
                  </Button>
                </>
              )}
            </div>
          </div>
          <div>
            <Text size="small" color="subdued">Criado em</Text>
            <Text>{formatDate(waba.created_at)}</Text>
          </div>
          <div>
            <Text size="small" color="subdued">Atualizado em</Text>
            <Text>{formatDate(waba.updated_at)}</Text>
          </div>
        </div>
      </Section>

      {/* Phone Numbers */}
      <Section
        title={`Telefones (${phones.length})`}
        headerActions={
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => { phoneForm.reset(); setCreatePhoneOpen(true) }}
            >
              <AddIcon />
              Adicionar
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleSyncPhones}
              disabled={syncing}
            >
              {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshIcon />}
              Sincronizar do Meta
            </Button>
          </div>
        }
      >
        {phones.length === 0 ? (
          <EmptyState
            title="Nenhum telefone"
            description="Adicione telefones manualmente ou sincronize do Meta."
            image={<PhoneIcon className="w-12 h-12" />}
          />
        ) : (
          <div className="-m-5">
            <GroupedTable
              groups={[]}
              items={phones}
              columns={phoneColumns}
              getGroupKey={() => ''}
              renderGroupCells={() => null}
              renderItemCells={renderPhoneCells}
              expandedGroups={new Set()}
              onGroupToggle={() => {}}
              isGrouped={false}
              pagination={phonePagination}
            />
          </div>
        )}
      </Section>

      {/* Templates */}
      <Section
        title={`Templates (${templates.length})`}
        headerActions={
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => { templateForm.reset(); setCreateTemplateOpen(true) }}
            >
              <AddIcon />
              Criar Template
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleSyncTemplates}
              disabled={syncingTemplates}
            >
              {syncingTemplates ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshIcon />}
              Sincronizar do Meta
            </Button>
          </div>
        }
      >
        {templates.length === 0 ? (
          <EmptyState
            title="Nenhum template"
            description="Crie templates manualmente ou sincronize do Meta."
            image={<FileIcon className="w-12 h-12" />}
          />
        ) : (
          <div className="-m-5">
            <GroupedTable
              groups={[]}
              items={templates}
              columns={templateColumns}
              getGroupKey={() => ''}
              renderGroupCells={() => null}
              renderItemCells={renderTemplateCells}
              expandedGroups={new Set()}
              onGroupToggle={() => {}}
              isGrouped={false}
              pagination={templatePagination}
            />
          </div>
        )}
      </Section>

      {/* Edit WABA Modal */}
      <Modal
        open={editWabaOpen}
        onClose={() => setEditWabaOpen(false)}
        title="Editar WABA"
        primaryAction={{
          content: 'Salvar',
          onAction: handleUpdateWaba,
          loading: editWabaForm.processing,
        }}
        secondaryAction={{
          content: 'Cancelar',
          onAction: () => setEditWabaOpen(false),
        }}
      >
        <div className="space-y-4">
          <Select
            label="Organização"
            options={orgOptions}
            value={editWabaForm.data.organization_id}
            onChange={(value) => editWabaForm.setData('organization_id', value)}
            placeholder="Selecione uma organização"
            error={editWabaForm.errors.organization_id}
          />
          <TextField
            label="External ID"
            value={editWabaForm.data.external_id}
            onChange={(value) => editWabaForm.setData('external_id', value)}
            error={editWabaForm.errors.external_id}
          />
          <TextField
            label="Nome"
            value={editWabaForm.data.name}
            onChange={(value) => editWabaForm.setData('name', value)}
            error={editWabaForm.errors.name}
            helpText="Opcional"
          />
        </div>
      </Modal>

      {/* Delete WABA Modal */}
      <Modal
        open={deleteWabaOpen}
        onClose={() => setDeleteWabaOpen(false)}
        title="Excluir WABA"
        destructiveAction={{
          content: 'Excluir',
          onAction: handleDeleteWaba,
        }}
        secondaryAction={{
          content: 'Cancelar',
          onAction: () => setDeleteWabaOpen(false),
        }}
      >
        <div className="space-y-2">
          <Text>
            Tem certeza que deseja excluir a WABA <strong>{waba.name || waba.external_id}</strong>?
          </Text>
          {phones.length > 0 && (
            <p className="text-sm text-red-600">
              Esta ação irá excluir {phones.length} telefone(s) associado(s).
            </p>
          )}
          {assignedPhones.length > 0 && (
            <p className="text-sm text-red-600">
              {assignedPhones.length} telefone(s) estão atribuídos a digital workers:{' '}
              {assignedPhones.map((p) => p.digital_worker_name).join(', ')}.
            </p>
          )}
        </div>
      </Modal>

      {/* Create Phone Modal */}
      <Modal
        open={createPhoneOpen}
        onClose={() => setCreatePhoneOpen(false)}
        title="Adicionar Telefone"
        primaryAction={{
          content: 'Criar',
          onAction: handleCreatePhone,
          loading: phoneForm.processing,
        }}
        secondaryAction={{
          content: 'Cancelar',
          onAction: () => setCreatePhoneOpen(false),
        }}
      >
        <div className="space-y-4">
          <TextField
            label="Nome verificado"
            value={phoneForm.data.verified_name}
            onChange={(value) => phoneForm.setData('verified_name', value)}
            error={phoneForm.errors.verified_name}
            helpText="Nome da empresa conforme registrado no Meta Business"
          />
          <TextField
            label="DDD"
            value={phoneForm.data.area_code}
            onChange={(value) => phoneForm.setData('area_code', value.replace(/\D/g, '').slice(0, 2))}
            error={phoneForm.errors.area_code}
            maxLength={2}
            helpText="Código de área (ex: 11, 21, 31)"
          />
        </div>
      </Modal>

      {/* Edit Phone Modal */}
      <Modal
        open={editPhoneOpen}
        onClose={() => setEditPhoneOpen(false)}
        title="Editar Telefone"
        primaryAction={{
          content: 'Salvar',
          onAction: handleUpdatePhone,
          loading: editPhoneForm.processing,
        }}
        secondaryAction={{
          content: 'Cancelar',
          onAction: () => setEditPhoneOpen(false),
        }}
      >
        <div className="space-y-4">
          <TextField
            label="Meta Phone Number ID"
            value={editPhoneForm.data.meta_phone_number_id}
            onChange={(value) => editPhoneForm.setData('meta_phone_number_id', value)}
            error={editPhoneForm.errors.meta_phone_number_id}
          />
          <TextField
            label="Número de exibição"
            value={editPhoneForm.data.display_phone_number}
            onChange={(value) => editPhoneForm.setData('display_phone_number', value)}
            error={editPhoneForm.errors.display_phone_number}
          />
          <TextField
            label="Nome verificado"
            value={editPhoneForm.data.verified_name}
            onChange={(value) => editPhoneForm.setData('verified_name', value)}
            error={editPhoneForm.errors.verified_name}
          />
          <TextField
            label="Status de verificação"
            value={editPhoneForm.data.verification_status}
            onChange={(value) => editPhoneForm.setData('verification_status', value)}
            error={editPhoneForm.errors.verification_status}
          />
          <TextField
            label="Qualidade"
            value={editPhoneForm.data.quality_rating}
            onChange={(value) => editPhoneForm.setData('quality_rating', value)}
            error={editPhoneForm.errors.quality_rating}
          />
        </div>
      </Modal>

      {/* Delete Phone Modal */}
      <Modal
        open={deletePhoneOpen}
        onClose={() => setDeletePhoneOpen(false)}
        title="Excluir Telefone"
        destructiveAction={{
          content: 'Excluir',
          onAction: handleDeletePhone,
        }}
        secondaryAction={{
          content: 'Cancelar',
          onAction: () => setDeletePhoneOpen(false),
        }}
      >
        <div className="space-y-2">
          <Text>
            Tem certeza que deseja excluir o telefone <strong>{selectedPhone?.display_phone_number || selectedPhone?.meta_phone_number_id}</strong>?
          </Text>
          {selectedPhone?.digital_worker_name && (
            <p className="text-sm text-red-600">
              Este telefone está atribuído ao digital worker <strong>{selectedPhone.digital_worker_name}</strong>.
            </p>
          )}
        </div>
      </Modal>

      {/* Create Template Modal */}
      <Modal
        open={createTemplateOpen}
        onClose={() => setCreateTemplateOpen(false)}
        title="Criar Template"
        primaryAction={{
          content: 'Criar',
          onAction: handleCreateTemplate,
          loading: templateForm.processing,
        }}
        secondaryAction={{
          content: 'Cancelar',
          onAction: () => setCreateTemplateOpen(false),
        }}
      >
        <div className="space-y-4">
          <TextField
            label="Nome"
            value={templateForm.data.name}
            onChange={handleTemplateNameChange}
            error={templateForm.errors.name}
            helpText="Apenas letras minúsculas, números e underscores"
          />
          <Select
            label="Idioma"
            options={LANGUAGE_OPTIONS}
            value={templateForm.data.language}
            onChange={(value) => templateForm.setData('language', value)}
            error={templateForm.errors.language}
          />
          <Select
            label="Categoria"
            options={CATEGORY_OPTIONS}
            value={templateForm.data.category}
            onChange={(value) => templateForm.setData('category', value)}
            error={templateForm.errors.category}
          />
          <TextField
            label="Cabeçalho"
            value={templateForm.data.header_text}
            onChange={(value) => templateForm.setData('header_text', value)}
            error={templateForm.errors.header_text}
            helpText="Opcional"
          />
          <TextField
            label="Corpo"
            value={templateForm.data.body_text}
            onChange={(value) => templateForm.setData('body_text', value)}
            error={templateForm.errors.body_text}
            multiline={4}
            helpText="Use {{1}}, {{2}}, etc. para variáveis"
          />
          <TextField
            label="Exemplos de variáveis"
            value={templateForm.data.body_example}
            onChange={(value) => templateForm.setData('body_example', value)}
            error={templateForm.errors.body_example}
            helpText="Valores separados por vírgula (ex: João, Produto X)"
          />
          <TextField
            label="Rodapé"
            value={templateForm.data.footer_text}
            onChange={(value) => templateForm.setData('footer_text', value)}
            error={templateForm.errors.footer_text}
            helpText="Opcional"
          />
        </div>
      </Modal>

      {/* Edit Template Modal */}
      <Modal
        open={editTemplateOpen}
        onClose={() => setEditTemplateOpen(false)}
        title="Editar Template"
        primaryAction={{
          content: 'Salvar',
          onAction: handleUpdateTemplate,
          loading: editTemplateForm.processing,
        }}
        secondaryAction={{
          content: 'Cancelar',
          onAction: () => setEditTemplateOpen(false),
        }}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Text size="small" color="subdued">Nome</Text>
              <Text>{selectedTemplate?.name}</Text>
            </div>
            <div>
              <Text size="small" color="subdued">Idioma</Text>
              <Text>{selectedTemplate?.language}</Text>
            </div>
            <div>
              <Text size="small" color="subdued">Categoria</Text>
              <Text>{selectedTemplate?.category}</Text>
            </div>
          </div>
          <TextField
            label="Cabeçalho"
            value={editTemplateForm.data.header_text}
            onChange={(value) => editTemplateForm.setData('header_text', value)}
            error={editTemplateForm.errors.header_text}
            helpText="Opcional"
          />
          <TextField
            label="Corpo"
            value={editTemplateForm.data.body_text}
            onChange={(value) => editTemplateForm.setData('body_text', value)}
            error={editTemplateForm.errors.body_text}
            multiline={4}
            helpText="Use {{1}}, {{2}}, etc. para variáveis"
          />
          <TextField
            label="Exemplos de variáveis"
            value={editTemplateForm.data.body_example}
            onChange={(value) => editTemplateForm.setData('body_example', value)}
            error={editTemplateForm.errors.body_example}
            helpText="Valores separados por vírgula (ex: João, Produto X)"
          />
          <TextField
            label="Rodapé"
            value={editTemplateForm.data.footer_text}
            onChange={(value) => editTemplateForm.setData('footer_text', value)}
            error={editTemplateForm.errors.footer_text}
            helpText="Opcional"
          />
        </div>
      </Modal>

      {/* Preview Template Modal */}
      <TemplatePreviewModal
        template={previewTemplate}
        onClose={() => setPreviewTemplate(null)}
      />

      {/* Delete Template Modal */}
      <Modal
        open={deleteTemplateOpen}
        onClose={() => setDeleteTemplateOpen(false)}
        title="Excluir Template"
        destructiveAction={{
          content: 'Excluir',
          onAction: handleDeleteTemplate,
        }}
        secondaryAction={{
          content: 'Cancelar',
          onAction: () => setDeleteTemplateOpen(false),
        }}
      >
        <div className="space-y-2">
          <Text>
            Tem certeza que deseja excluir o template <strong>{selectedTemplate?.name}</strong> ({selectedTemplate?.language})?
          </Text>
          <p className="text-sm text-red-600">
            Esta ação irá excluir o template tanto localmente quanto no Meta.
          </p>
        </div>
      </Modal>
    </div>
  )
}
